import React, {
  ChangeEventHandler,
  ForwardedRef,
  ReactElement,
  ReactNode,
} from "react";
import styled from "styled-components/macro";
import { Tooltip } from "@src/components/elements/Tooltip";

const StyledRoot = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;

  label {
    position: relative;
    cursor: pointer;
    font-family: var(--inter-font);
    font-size: var(--font-size-small);
    display: flex;
    align-items: center;
    user-select: none;
    height: 1.25rem;
    line-height: 1em;

    input {
      margin: 0.1rem 0.5rem 0 0;
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;

      &:checked ~ .checkmark:after {
        display: block;
      }
    }

    input:checked ~ .checkmark,
    &:hover input:checked ~ .checkmark {
      background-color: var(--focus-green);
      border-color: var(--focus-green);
    }
    &:hover input:disabled ~ .checkmark {
      background-color: var(--border-gray);
      border-color: var(--border-gray);
      cursor: default;
    }

    input:disabled ~ .checkmark {
      background-color: var(--border-gray);
      border-color: var(--border-gray);
    }

    &:hover input ~ .checkmark {
      border-color: var(--focus-green);
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 1.25rem;
      width: 1.25rem;
      background-color: white;
      border-radius: var(--rounded-corner);
      border: 1px solid var(--border-gray);

      &:after {
        content: "";
        position: absolute;
        left: 6px;
        top: 3px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
      }
    }

    input:disabled ~ .checkmark:after {
      opacity: 0;
    }

    .checkbox-label {
      padding-left: 1.5rem;
    }
  }

  .error {
    color: var(--error-color);
    font-family: var(--inter-font);
    font-size: 0.8em;
  }
`;

export interface CheckboxInputProps {
  value: boolean;
  label: ReactNode | string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClick?: () => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
  error?: string;
  canShowError?: boolean;
  disabled?: boolean;
  disabledTooltipContent?: string;
}

export const Checkbox = React.forwardRef(
  (
    {
      label,
      value,
      placeholder,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onChange = () => {},
      required,
      className,
      error,
      canShowError = true,
      onClick,
      disabled,
      disabledTooltipContent,
    }: CheckboxInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ): ReactElement => {
    const main = (
      <label>
        <input
          type="checkbox"
          ref={ref}
          name={String(label)}
          checked={value}
          placeholder={placeholder}
          onChange={onChange}
          required={required}
          disabled={!!disabled}
          className={`${error ? "field-error" : ""}`}
        />
        <span className="checkmark"></span>
        <span className="checkbox-label">
          {label} {required && <span className="error">*</span>}
        </span>
      </label>
    );
    return (
      <StyledRoot className={className} onClick={onClick} disabled={disabled}>
        {disabled && disabledTooltipContent && (
          <Tooltip showWithoutOverflow={true} title={disabledTooltipContent}>
            {main}
          </Tooltip>
        )}
        {disabled && !disabledTooltipContent ? main : null}
        {!disabled && main}

        {canShowError && <div className="error">{error}</div>}
      </StyledRoot>
    );
  },
);

Checkbox.displayName = "Checkbox";
